import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, LogIn, Mail } from "lucide-react";

// Shown when an account already exists for the entered email.
export function NeedsLoginPanel({ message, redirectTo }) {
  return (
    <div data-testid="needs-login-panel" className="border border-amber-300 bg-amber-50 rounded-sm p-6">
      <div className="flex items-start gap-3">
        <LogIn className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-xl uppercase tracking-tight text-[#0A192F]">Account Found</div>
          <p className="text-sm text-slate-700 mt-2">{message}</p>
          <Link to="/login" state={{ redirectTo }} data-testid="needs-login-btn"
            className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a]">
            <LogIn className="w-4 h-4" /> Log In & Continue
          </Link>
        </div>
      </div>
    </div>
  );
}

// Shown when a name + DOB match is found: "Is this you?"
export function DuplicatePrompt({ candidates, message, onPickExisting, onCreateNew, busy }) {
  return (
    <div data-testid="duplicate-prompt" className="border border-amber-300 bg-amber-50 rounded-sm p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-display text-xl uppercase tracking-tight text-[#0A192F]">Is This You?</div>
          <p className="text-sm text-slate-700 mt-2">{message}</p>
          <div className="mt-4 space-y-2">
            {candidates.map((c) => (
              <div key={c.id} data-testid={`dup-candidate-${c.id}`} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-sm p-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {[c.graduation_year && `Class of ${c.graduation_year}`, c.province, c.current_team].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <button type="button" data-testid={`dup-confirm-${c.id}`} disabled={busy} onClick={() => onPickExisting(c.id)}
                  className="shrink-0 px-3 py-2 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a] disabled:opacity-60">
                  Yes, that's me
                </button>
              </div>
            ))}
          </div>
          <button type="button" data-testid="dup-create-new-btn" disabled={busy} onClick={onCreateNew}
            className="mt-4 text-sm font-semibold text-[#E63946] uppercase tracking-wider disabled:opacity-60">
            None of these — create a new profile
          </button>
        </div>
      </div>
    </div>
  );
}

export function SuccessScreen({ title, message, claimRequired, playerSlug }) {
  return (
    <div data-testid="interest-success" className="text-center py-8">
      <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
      <h2 className="font-display text-3xl uppercase tracking-tight text-[#0A192F] mt-4">{title}</h2>
      <p className="text-sm text-slate-700 mt-3 max-w-md mx-auto">{message}</p>
      {claimRequired && (
        <div className="mt-6 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-700">
          <Mail className="w-4 h-4 text-[#E63946]" />
          Check your email to claim and set up your player account.
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        {playerSlug && (
          <Link to={`/prospects/${playerSlug}`} data-testid="success-view-profile"
            className="px-5 py-3 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm hover:border-[#0A192F]">
            View Profile
          </Link>
        )}
        <Link to="/" data-testid="success-home"
          className="px-5 py-3 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a]">
          Back to Home
        </Link>
      </div>
    </div>
  );
}
